import React from "react";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ArrowDropDown,
  AssignmentTurnedInOutlined,
  DownloadOutlined,
  EditOutlined,
  Print,
} from "@mui/icons-material";
import { toast } from "react-toastify";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import {
  useGetDeliveryDetailQuery,
  useListShipmentsByDeliveryIdQuery,
} from "store/services/deliveryApi";
import {
  DeliveryDetailContext,
  useDeliveryDetailContext,
} from "contexts/DeliveryDetailContext";

import { useAuth } from "hooks";
import DeliveryBasicDetail from "./DeliveryComponents/DeliveryBasicDetail";
import DeliveryDetailTab from "./DeliveryComponents/DeliveryDetailTab";
import DeliveryDetailClassicView from "./DeliveryComponents/DeliveryDetailClassicView";
import {
  generateDeliveryPodImage,
  generateDeliveryPodPrint,
} from "./DeliveryComponents/DeliveryPodPrint";

const DeliveryDetailScreen = () => {
  const { isDriver } = useAuth();
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const breadcrumbs = [
    {
      title: "Deliveries",
      path: !isDriver ? "/app/crm/delivery/list" : "/app/driver/delivery",
    },
    { title: "Delivery Detail" },
  ];

  // RTK Query
  const {
    data: deliveryDetail,
    isLoading: loadingDelivery,
    error,
  } = useGetDeliveryDetailQuery(id);

  return (
    <DeliveryDetailContext.Provider value={{ deliveryDetail, loadingDelivery }}>
      <BreadcrumbLayout
        breadcrumbs={breadcrumbs}
        isBusy={loadingDelivery}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />

        <BreadcrumbLayout.Paper>
          {isMobile ? (
            <DeliveryDetailClassicView />
          ) : (
            <>
              <DeliveryBasicDetail />
              <Divider />
              <DeliveryDetailTab />
            </>
          )}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </DeliveryDetailContext.Provider>
  );
};

export default DeliveryDetailScreen; // Updated export name

const PageActions = () => {
  const [podAnchorEl, setPodAnchorEl] = React.useState(null);
  const [downloadingPod, setDownloadingPod] = React.useState(false);

  const { deliveryDetail } = useDeliveryDetailContext(); // Updated context hook

  const { isVendor, isDriver, viewEntity } = useAuth();

  const { data: shipmentRes, isLoading: loadingShipments } =
    useListShipmentsByDeliveryIdQuery(
      { deliveryId: deliveryDetail?._id, page: 1, limit: 9999 },
      { skip: !deliveryDetail?._id }
    );
  const shipments = shipmentRes?.data || [];

  const basePath = isVendor ? "/app/vendor/delivery" : "/app/crm/delivery"; // Updated base path

  const handleViewPod = () => {
    generateDeliveryPodPrint(deliveryDetail, shipments, viewEntity?.entity);
    setPodAnchorEl(null);
  };

  const handleDownloadPod = async () => {
    setPodAnchorEl(null);
    setDownloadingPod(true);
    try {
      await generateDeliveryPodImage(
        deliveryDetail,
        shipments,
        viewEntity?.entity
      );
      toast.success("Proof of Delivery image downloaded");
    } catch (error) {
      toast.error("Could not download Proof of Delivery image");
    } finally {
      setDownloadingPod(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <Button
        startIcon={<AssignmentTurnedInOutlined />}
        endIcon={<ArrowDropDown />}
        variant="contained"
        color="primary"
        onClick={(event) => setPodAnchorEl(event.currentTarget)}
        disabled={
          !deliveryDetail || loadingShipments || downloadingPod
        }
      >
        Proof of Delivery
      </Button>
      <Menu
        anchorEl={podAnchorEl}
        open={Boolean(podAnchorEl)}
        onClose={() => setPodAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className="mt-3"
      >
        <MenuItem onClick={handleViewPod} disabled={!deliveryDetail}>
          <Print sx={{ mr: 1 }} /> View / Print
        </MenuItem>
        <MenuItem
          onClick={handleDownloadPod}
          disabled={!deliveryDetail || downloadingPod}
        >
          <DownloadOutlined sx={{ mr: 1 }} /> Download Image (PNG)
        </MenuItem>
      </Menu>
      {!isDriver && (
        <Link
          to={`${basePath}/${deliveryDetail?._id}/edit`} // Updated path
          style={{ pointerEvents: isVendor ? "none" : "auto" }}
        >
          <Button
            startIcon={<EditOutlined />}
            variant="contained"
            disabled={isVendor}
            sx={{ opacity: isVendor ? 0.5 : 1 }}
          >
            Edit
          </Button>
        </Link>
      )}
    </div>
  );
};
